/**
 * Per-handle personal best, stored locally. No server round-trip.
 *
 * Keyed by lowercase handle. localStorage is the source of truth on the
 * device; if the user clears storage they lose the history (acceptable for
 * a portfolio piece — leaderboard remains authoritative cross-device).
 */

const KEY = "aether.bests";

type BestsMap = Record<string, number>;

function load(): BestsMap {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as BestsMap) : {};
  } catch {
    return {};
  }
}

function save(m: BestsMap) {
  try {
    localStorage.setItem(KEY, JSON.stringify(m));
  } catch {
    // Quota / private mode — silently drop. PB is best-effort.
  }
}

export function getBest(handle: string): number {
  if (!handle) return 0;
  return load()[handle.toLowerCase()] ?? 0;
}

/** Returns true iff the new score beat the previous best (or was the first). */
export function recordScore(handle: string, score: number): boolean {
  if (!handle) return false;
  const m = load();
  const k = handle.toLowerCase();
  const prev = m[k] ?? 0;
  if (score > prev) {
    m[k] = score;
    save(m);
    return true;
  }
  return false;
}
